import { useEffect, useMemo, useState } from "react";
import { api } from "./api.ts";
import type { IndexItem, IndexResponse, Status } from "./types.ts";
import { parse, stringify, withStatus } from "./frontmatter.ts";
import { Editor } from "./editor.tsx";
import { NewArticle } from "./new.tsx";
import { FolderManager } from "./folders.tsx";
import { Landing } from "./landing.tsx";

type Filter = "all" | Status | "unfinished";

const STATUS_LABEL: Record<Status, string> = {
  published: "已發表",
  draft: "草稿",
  unlisted: "未列出",
  archived: "封存",
};

const CACHE_KEY = "still-cms-index";

// Navbar order; unknown categories fall to the end.
const CATEGORY_ORDER = ["Journey", "Homelab", "Selfhost", "Blog", "Access"];
const catRank = (c: string) => {
  const i = CATEGORY_ORDER.indexOf(c);
  return i < 0 ? 99 : i;
};

// 手機 metadata 行用：blog 顯示「發表」日、docs 顯示「更新」日；當年省略年份。
function whenMeta(it: IndexItem): { prefix: "發表" | "更新"; text: string } | null {
  const iso = it.date ?? it.updated;
  if (!iso) return null;
  const [y, m, d] = iso.slice(0, 10).split("-");
  const currentYear = String(new Date().getFullYear());
  return { prefix: it.date ? "發表" : "更新", text: y === currentYear ? `${m}.${d}` : `${y}.${m}.${d}` };
}

/* ── 一致的 topbar icon（inline SVG，currentColor） ── */
const IconPlus = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5v14M5 12h14" />
  </svg>
);
const IconMenu = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);
const IconDeploy = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
    <path d="M12 15l-3-3a22 22 0 0 1 8-10c1.5.5 3.5 2.5 4 4a22 22 0 0 1-10 8z" />
    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
  </svg>
);
const IconRefresh = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12a9 9 0 1 1-2.64-6.36" />
    <path d="M21 3v6h-6" />
  </svg>
);
const IconLogout = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="M16 17l5-5-5-5" />
    <path d="M21 12H9" />
  </svg>
);
const IconClose = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);
const IconFolder = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </svg>
);

export function App() {
  const [index, setIndex] = useState<IndexResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [managingFolders, setManagingFolders] = useState(false);
  const [me, setMe] = useState<{
    authEnabled: boolean;
    user: { name: string; email: string } | null;
    canView: boolean;
    canEdit: boolean;
    guest: boolean;
    viewPin: boolean;
    viewPinLen: number;
  } | null>(null);

  const [filter, setFilter] = useState<Filter>("all");
  const [category, setCategory] = useState<string>("all");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<"default" | "updated" | "title">("default");
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem("still-cms-collapsed") || "[]"));
    } catch {
      return new Set();
    }
  });

  const toggleCollapse = (c: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      localStorage.setItem("still-cms-collapsed", JSON.stringify([...next]));
      return next;
    });

  // 每列動作選單（⋯）：anchor 為 null → 手機 bottom sheet；有 rect → 桌機 popover。
  const [menu, setMenu] = useState<{ item: IndexItem; anchor: DOMRect | null } | null>(null);
  const [deleting, setDeleting] = useState<IndexItem | null>(null);
  const [busyPath, setBusyPath] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [topMenu, setTopMenu] = useState<{ anchor: DOMRect | null } | null>(null);
  const [deploying, setDeploying] = useState(false);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(t);
  }, [notice]);

  // Esc 關閉任何開著的浮層——確認框優先，其次是列動作選單，最後是頂部 ≡ 選單。
  useEffect(() => {
    if (!deleting && !menu && !topMenu) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      if (deleting) setDeleting(null);
      else if (menu) setMenu(null);
      else if (topMenu) setTopMenu(null);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [deleting, menu, topMenu]);

  async function load(force = false) {
    setLoading(true);
    setError(null);
    try {
      const data = await api.index();
      setIndex(data);
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch (e) {
      setError(String((e as Error).message));
      if (!force) {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) setIndex(JSON.parse(cached));
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    api
      .me()
      .then((m) => {
        setMe(m);
        if (m.canView) {
          const cached = localStorage.getItem(CACHE_KEY);
          if (cached) {
            try {
              setIndex(JSON.parse(cached));
            } catch {}
          }
          load(true);
        } else {
          setLoading(false); // 無存取權 → 落地頁
        }
      })
      .catch(() => setLoading(false));
  }, []);

  const categories = useMemo(
    () =>
      Array.from(new Set(index?.items.map((i) => i.category) ?? [])).sort(
        (a, b) => catRank(a) - catRank(b) || a.localeCompare(b)
      ),
    [index]
  );

  // 每篇文章所屬的「路徑資料夾」——用 _category_.json 的 label，沒有就用資料夾名；
  // 直接在分類根目錄（docs/<cat> 或 blog 根）的文章不標。
  const folderOf = useMemo(() => {
    const byDir = new Map<string, string>();
    for (const f of index?.folders ?? []) byDir.set(f.dir, f.label);
    return (it: IndexItem): string | null => {
      const dir = it.path.slice(0, it.path.lastIndexOf("/"));
      if (byDir.has(dir)) return byDir.get(dir)!;
      const segs = it.path.split("/");
      if (it.path.startsWith("docs/") && segs.length > 3) return segs[segs.length - 2];
      return null;
    };
  }, [index]);

  // Every tag already used across the repo, most-used first — for the editor's picker.
  const allTags = useMemo(() => {
    const count = new Map<string, number>();
    for (const it of index?.items ?? []) for (const t of it.tags) count.set(t, (count.get(t) ?? 0) + 1);
    return [...count.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map((e) => e[0]);
  }, [index]);

  const counts = useMemo(() => {
    const c = { all: 0, published: 0, draft: 0, unlisted: 0, unfinished: 0, archived: 0 };
    for (const it of index?.items ?? []) {
      if (it.status === "archived") {
        c.archived++;
        continue; // 封存不計入其他桶
      }
      c.all++;
      c[it.status]++;
      if (it.emptyish) c.unfinished++;
    }
    return c;
  }, [index]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (index?.items ?? []).filter((it) => {
      // 封存只在「封存」篩選出現；其他篩選一律排除封存
      if (it.status === "archived" ? filter !== "archived" : filter === "archived") return false;
      if (category !== "all" && it.category !== category) return false;
      if (filter === "unfinished" && !it.emptyish) return false;
      if (filter !== "all" && filter !== "unfinished" && it.status !== filter) return false;
      if (q && !(it.title.toLowerCase().includes(q) || it.path.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [index, filter, category, query]);

  const grouped = useMemo(() => {
    const map = new Map<string, IndexItem[]>();
    for (const it of visible) {
      const key = it.category;
      (map.get(key) ?? map.set(key, []).get(key)!).push(it);
    }
    const within =
      sort === "updated"
        ? (a: IndexItem, b: IndexItem) => (b.updated ?? "").localeCompare(a.updated ?? "")
        : sort === "title"
        ? (a: IndexItem, b: IndexItem) => a.title.localeCompare(b.title, "zh-Hant")
        : null; // "default" keeps the server order (blog by date, docs by sidebar order)
    if (within) for (const items of map.values()) items.sort(within);
    return Array.from(map.entries()).sort(
      (a, b) => catRank(a[0]) - catRank(b[0]) || a[0].localeCompare(b[0])
    );
  }, [visible, sort]);

  const filtersActive = filter !== "all" || category !== "all" || query.trim() !== "";
  const hasAny = (index?.items.length ?? 0) > 0;
  const clearFilters = () => {
    setFilter("all");
    setCategory("all");
    setQuery("");
  };

  const canEdit = me?.canEdit ?? false;
  async function leaveGuest() {
    try {
      await api.leave();
      window.location.reload();
    } catch (e) {
      setError(String((e as Error).message));
    }
  }

  async function doDeploy() {
    setTopMenu(null);
    if (!canEdit) {
      setNotice("訪客唯讀模式——這個動作不會送出，登入後即可部署。");
      return;
    }
    setDeploying(true);
    try {
      await api.deploy();
      setNotice("已觸發部署——在 Forgejo Actions 可看進度，完成後正式站會更新。");
    } catch (e) {
      setError(String((e as Error).message));
    } finally {
      setDeploying(false);
    }
  }

  function onSaved(path: string, sha: string, status: Status) {
    setIndex((prev) =>
      prev
        ? { ...prev, items: prev.items.map((it) => (it.path === path ? { ...it, sha, status } : it)) }
        : prev
    );
  }

  // 從列表直接改狀態/封存：抓檔案 → 改 front matter → 存（伺服器端 commit）
  // 訪客也看得到並可點這個選單，只是這裡會攔下、不會真的送出 commit。
  async function applyStatus(it: IndexItem, status: Status) {
    setMenu(null);
    if (it.status === status) return;
    if (!canEdit) {
      setNotice("訪客唯讀模式——這個動作不會送出，登入後即可編輯。");
      return;
    }
    setBusyPath(it.path);
    try {
      const f = await api.file(it.path);
      const p = parse(f.content);
      const content = stringify(withStatus(p.data, status), p.body);
      const res = await api.save({ path: it.path, content, sha: f.sha, message: `content: ${status} ${it.path}` });
      onSaved(it.path, res.sha, status);
    } catch (e) {
      setError(String((e as Error).message));
    } finally {
      setBusyPath(null);
    }
  }

  async function doDelete(it: IndexItem) {
    setDeleting(null);
    if (!canEdit) {
      setNotice("訪客唯讀模式——這個動作不會送出，登入後即可編輯。");
      return;
    }
    setBusyPath(it.path);
    try {
      await api.remove({ path: it.path, sha: it.sha });
      setIndex((prev) => (prev ? { ...prev, items: prev.items.filter((x) => x.path !== it.path) } : prev));
    } catch (e) {
      setError(String((e as Error).message));
    } finally {
      setBusyPath(null);
    }
  }

  if (me === null) return <div className="app boot" aria-busy="true" />;
  if (me.authEnabled && !me.canView) return <Landing showPin={me.viewPin} pinLen={me.viewPinLen} />;

  if (editing) {
    const item = index?.items.find((i) => i.path === editing) ?? null;
    return (
      <Editor
        path={editing}
        initialStatus={item?.status ?? "published"}
        allTags={allTags}
        canEdit={canEdit}
        onClose={() => setEditing(null)}
        onSaved={onSaved}
      />
    );
  }

  if (creating) {
    return (
      <NewArticle
        categories={categories}
        items={index?.items ?? []}
        folders={index?.folders ?? []}
        canEdit={canEdit}
        onClose={() => setCreating(false)}
        onCreated={(path) => {
          setCreating(false);
          load();
          setEditing(path);
        }}
      />
    );
  }

  if (managingFolders) {
    return (
      <FolderManager
        categories={categories}
        items={index?.items ?? []}
        folders={index?.folders ?? []}
        treeSha={index?.treeSha ?? ""}
        canEdit={canEdit}
        onClose={() => setManagingFolders(false)}
        onReload={() => load(true)}
        onOpenDoc={(path) => { setManagingFolders(false); setEditing(path); }}
      />
    );
  }

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <h1>live, still<span className="dot">.</span></h1>
        </div>
        <div className="actions">
          {me?.guest && <span className="role-tag">訪客・唯讀</span>}
          <button
            className="icon-btn"
            aria-label="更多"
            aria-haspopup="menu"
            onClick={(e) =>
              setTopMenu({ anchor: window.innerWidth <= 540 ? null : e.currentTarget.getBoundingClientRect() })
            }
          >
            <IconMenu />
          </button>
          <button className="btn primary new-btn" onClick={() => setCreating(true)}>
            <IconPlus />
            <span className="new-btn__label">新文章</span>
          </button>
        </div>
      </header>

      {error && <div className="banner error">連線問題：{error}（顯示的是上次快取）</div>}
      {notice && <div className="banner notice">{notice}</div>}

      <div className="filters">
        <span className="filter-label">狀態</span>
        {(["all", "draft", "published", "unlisted", "unfinished", "archived"] as Filter[]).map((f) => (
          <button
            key={f}
            className={`chip ${filter === f ? "on" : ""}`}
            onClick={() => setFilter(f)}
          >
            {f === "all" ? "全部" : f === "unfinished" ? "未完成" : STATUS_LABEL[f as Status]}
            <span className="n">{counts[f]}</span>
          </button>
        ))}
      </div>

      <div className="filters second">
        <span className="filter-label">分類</span>
        <button className={`chip ${category === "all" ? "on" : ""}`} onClick={() => setCategory("all")}>
          全部
        </button>
        {categories.map((c) => (
          <button key={c} className={`chip ${category === c ? "on" : ""}`} onClick={() => setCategory(c)}>
            {c}
          </button>
        ))}
      </div>

      <div className="listbar">
        <div className="search-wrap">
          <input
            className="search"
            placeholder="搜尋標題或路徑…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button className="search-clear" aria-label="清除搜尋" onClick={() => setQuery("")}>
              ✕
            </button>
          )}
        </div>
        <select className="sortsel" value={sort} onChange={(e) => setSort(e.target.value as typeof sort)}>
          <option value="default">預設順序</option>
          <option value="updated">最近修改</option>
          <option value="title">標題</option>
        </select>
      </div>

      <main className="list">
        {grouped.length === 0 && !loading && (
          <div className="empty">
            {!hasAny ? (
              <>
                <p className="empty-line">還沒有任何文章。</p>
                <button className="btn primary" onClick={() => setCreating(true)}>
                  ＋ 寫第一篇
                </button>
              </>
            ) : filtersActive ? (
              <>
                <p className="empty-line">沒有符合目前篩選的文章。</p>
                <button className="btn small ghost" onClick={clearFilters}>
                  清除篩選
                </button>
              </>
            ) : (
              <p className="empty-line">這個分類還沒有文章。</p>
            )}
          </div>
        )}
        {grouped.map(([group, items]) => {
          const isCollapsed = collapsed.has(group);
          return (
            <section key={group} className="group">
              <button
                className="group-head"
                onClick={() => toggleCollapse(group)}
                aria-expanded={!isCollapsed}
              >
                <span className="group-name">{group}</span>
                <span className="group-count">{items.length}</span>
                <span className={`group-chev ${isCollapsed ? "closed" : ""}`} aria-hidden>
                  ▾
                </span>
              </button>
              {!isCollapsed &&
                items.map((it) => {
                  const w = whenMeta(it);
                  const d = it.date ?? it.updated;
                  const busy = busyPath === it.path;
                  return (
                  <div key={it.path} className={`row ${it.status} ${busy ? "busy" : ""}`}>
                    <button className="row-hit" onClick={() => setEditing(it.path)} disabled={busy}>
                      <span
                        className="row-date"
                        title={w ? `${w.prefix}於 ${d!.slice(0, 10).replaceAll("-", ".")}` : undefined}
                      >
                        {d ? d.replaceAll("-", ".") : "—"}
                      </span>
                      <span className="row-main">
                        <span className="row-title">
                          {it.title || <em className="muted">（無標題）</em>}
                          {it.emptyish && <span className="flag">未完成</span>}
                        </span>
                        <span className="row-meta">
                          {folderOf(it) && <span className="row-folder" title="所在路徑資料夾">▤ {folderOf(it)}</span>}
                          <span className="row-path">{it.path.split("/").pop()}</span>
                        </span>
                      </span>
                      <span className="row-end">
                        <span className={`status ${it.status}`}>{STATUS_LABEL[it.status]}</span>
                        {w && (
                          <span className="row-when">
                            <span className="row-when__k">{w.prefix}</span> {w.text}
                          </span>
                        )}
                      </span>
                    </button>
                    <button
                      className="row-menu-btn"
                      aria-label="動作選單"
                      aria-haspopup="menu"
                      disabled={busy}
                      onClick={(e) =>
                        setMenu({
                          item: it,
                          anchor: window.innerWidth <= 540 ? null : e.currentTarget.getBoundingClientRect(),
                        })
                      }
                    >
                      {busy ? "…" : "⋯"}
                    </button>
                  </div>
                  );
                })}
            </section>
          );
        })}
      </main>

      {topMenu && (
        <div
          className={`sheet-backdrop ${topMenu.anchor ? "" : "menu-overlay"}`}
          onClick={() => setTopMenu(null)}
        >
          <div
            className={`action-menu ${topMenu.anchor ? "popover" : "fullscreen"}`}
            style={
              topMenu.anchor
                ? { top: topMenu.anchor.bottom + 6, right: Math.max(12, window.innerWidth - topMenu.anchor.right) }
                : undefined
            }
            onClick={(e) => e.stopPropagation()}
            role="menu"
          >
            {!topMenu.anchor && (
              <div className="menu-overlay__head">
                <span className="menu-overlay__brand">live, still<span className="dot">.</span></span>
                <button className="menu-overlay__close" aria-label="關閉" onClick={() => setTopMenu(null)}>
                  <IconClose />
                </button>
              </div>
            )}
            <nav className="menu-overlay__items">
              <button className="action-item" role="menuitem" style={{ ["--i" as any]: 0 }} onClick={() => { setTopMenu(null); load(); }}>
                <span className="action-item__icon"><IconRefresh /></span>
                <span className="action-item__body">
                  <span className="action-item__label">Refresh</span>
                  <span className="action-item__sub">重新載入清單</span>
                </span>
                <span className="action-item__go" aria-hidden="true">›</span>
              </button>
              <button className="action-item" role="menuitem" style={{ ["--i" as any]: 1 }} onClick={() => { setTopMenu(null); setManagingFolders(true); }}>
                <span className="action-item__icon"><IconFolder /></span>
                <span className="action-item__body">
                  <span className="action-item__label">Path</span>
                  <span className="action-item__sub">路徑編輯・增刪・排序</span>
                </span>
                <span className="action-item__go" aria-hidden="true">›</span>
              </button>
              <button className="action-item accent" role="menuitem" style={{ ["--i" as any]: 2 }} onClick={doDeploy} disabled={deploying}>
                <span className="action-item__icon"><IconDeploy /></span>
                <span className="action-item__body">
                  <span className="action-item__label">{deploying ? "Deploying…" : "Deploy"}</span>
                  <span className="action-item__sub">發佈內容到網站</span>
                </span>
                <span className="action-item__go" aria-hidden="true">›</span>
              </button>
              <div className="action-sep" role="separator" />
              {me?.guest ? (
                <button className="action-item" role="menuitem" style={{ ["--i" as any]: 3 }} onClick={() => { setTopMenu(null); leaveGuest(); }}>
                  <span className="action-item__icon"><IconLogout /></span>
                  <span className="action-item__body">
                    <span className="action-item__label">離開</span>
                    <span className="action-item__sub">結束參觀</span>
                  </span>
                  <span className="action-item__go" aria-hidden="true">›</span>
                </button>
              ) : (
                <a className="action-item" role="menuitem" style={{ ["--i" as any]: 3 }} href="/auth/logout">
                  <span className="action-item__icon"><IconLogout /></span>
                  <span className="action-item__body">
                    <span className="action-item__label">登出</span>
                    <span className="action-item__sub">結束這次編輯</span>
                  </span>
                  <span className="action-item__go" aria-hidden="true">›</span>
                </a>
              )}
            </nav>
          </div>
        </div>
      )}

      {menu && (
        <div className="sheet-backdrop" onClick={() => setMenu(null)}>
          <div
            className={`action-menu ${menu.anchor ? "popover" : "sheet"}`}
            style={
              menu.anchor
                ? { top: menu.anchor.bottom + 6, right: Math.max(12, window.innerWidth - menu.anchor.right) }
                : undefined
            }
            onClick={(e) => e.stopPropagation()}
            role="menu"
          >
            <p className="action-title">{menu.item.title || menu.item.path.split("/").pop()}</p>
            {(["published", "draft", "unlisted"] as Status[])
              .filter((s) => s !== menu.item.status && !(menu.item.status === "archived" && s === "draft"))
              .map((s) => (
                <button key={s} className="action-item" role="menuitem" onClick={() => applyStatus(menu.item, s)}>
                  設為{STATUS_LABEL[s]}
                </button>
              ))}
            {menu.item.status === "archived" ? (
              <button className="action-item" role="menuitem" onClick={() => applyStatus(menu.item, "draft")}>
                取消封存（回草稿）
              </button>
            ) : (
              <button className="action-item" role="menuitem" onClick={() => applyStatus(menu.item, "archived")}>
                封存
              </button>
            )}
            <button
              className="action-item danger"
              role="menuitem"
              onClick={() => {
                const it = menu.item;
                setMenu(null);
                setDeleting(it);
              }}
            >
              刪除…
            </button>
          </div>
        </div>
      )}

      {deleting && (
        <div className="sheet-backdrop center" onClick={() => setDeleting(null)}>
          <div className="confirm" onClick={(e) => e.stopPropagation()} role="alertdialog" aria-modal="true">
            <p className="confirm-title">確定刪除〈{deleting.title || deleting.path.split("/").pop()}〉？</p>
            <p className="confirm-body">檔案會從 repo 移除（git 歷史仍可救回）。</p>
            <div className="confirm-actions">
              <button className="btn small ghost" onClick={() => setDeleting(null)}>
                取消
              </button>
              <button className="btn small danger" onClick={() => doDelete(deleting)}>
                刪除
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
